<!-- File: src/components/GlobalSearch.vue -->
<template>
  <div class="global-search-wrapper" ref="wrapper">
    
    <!-- Search Input -->
    <div class="search-container">
      <div class="search-input-wrapper">
        
        <!-- Search Icon -->
        <Search class="search-icon" />

        <!-- Input Field -->
        <input
          ref="searchInput"
          v-model="query"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
          @keydown="onKeyDown"
          type="text"
          placeholder="Search commands, sessions, events..."
          class="search-input"
          autocomplete="off"
          spellcheck="false"
        />
        
        <!-- Loading Indicator -->
        <div v-if="loading" class="search-loading">
          <div class="spinner-small"></div>
        </div>
        
        <!-- Clear Button -->
        <button
          v-if="query && !loading"
          @click="clear"
          class="search-clear"
          type="button"
        >
          <X class="w-4 h-4" />
        </button>

        <!-- Shortcut Hint -->
        <div v-if="!isFocused && !query" class="shortcut-hint">
          <kbd>{{ isMac ? "⌘" : "Ctrl" }}</kbd>
          <kbd>K</kbd>
        </div>

      </div>
    </div>

    <!-- Results Dropdown -->
    <transition name="dropdown">
      <div
        v-if="showResults"
        class="results-dropdown custom-scrollbar"
        @mousedown.prevent
      >
        
        <!-- Loading State -->
        <div v-if="loading" class="result-loading">
          <div class="spinner-large"></div>
          <p class="text-xs text-dark-secondary mt-3">Searching...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="searchError" class="result-error">
          <AlertTriangle class="w-8 h-8 text-error mb-2" />
          <p class="text-xs text-error">{{ searchError }}</p>
          <button @click="retry" class="retry-button">
            Retry
          </button>
        </div>

        <!-- No Results -->
        <div v-else-if="!hasAnyResults && query.length >= 2" class="result-empty">
          <SearchX class="w-10 h-10 text-dark-secondary opacity-40 mb-3" />
          <p class="text-sm text-dark-primary mb-1">No results found</p>
          <p class="text-xs text-dark-secondary">Try a different search term</p>
        </div>

        <!-- Results Content -->
        <div v-else-if="hasAnyResults" class="results-content">
          
          <!-- Commands Section -->
          <div v-if="commandResults.length > 0" class="result-section">
            <div class="section-header">
              <div class="section-title">
                <Terminal class="w-4 h-4" />
                <span>COMMANDS</span>
                <span class="section-count">({{ commandsTotal }})</span>
              </div>
              <button
                v-if="commandsTotal > commandResults.length"
                @click="viewAllCommands"
                class="view-all-button"
              >
                View all
                <ArrowRight class="w-3 h-3" />
              </button>
            </div>
            
            <div class="section-items">
              <div
                v-for="(result, index) in commandResults"
                :key="'cmd-' + index"
                @click="selectCommand(result)"
                @mouseenter="hoveredIndex = 'cmd-' + index"
                :class="{ hovered: hoveredIndex === 'cmd-' + index }"
                class="result-item"
              >
                <div class="item-icon">
                  <ChevronRight class="w-4 h-4" />
                </div>
                <div class="item-content">
                  <code class="item-command">{{ result.command }}</code>
                  <div class="item-meta">
                    <span class="meta-session">{{ formatJobId(result.job_id) }}</span>
                    <span class="meta-separator">•</span>
                    <span class="meta-time">{{ formatTimestamp(result.timestamp) }}</span>
                  </div>
                </div>
                <div class="item-action">
                  <CornerDownLeft class="w-3 h-3" />
                </div>
              </div>
            </div>
          </div>

          <!-- Vision/OCR Section -->
          <div v-if="visionResults.length > 0" class="result-section">
            <div class="section-header">
              <div class="section-title">
                <Eye class="w-4 h-4" />
                <span>VISION</span>
                <span class="section-count">({{ visionTotal }})</span>
              </div>
              <button
                v-if="visionTotal > visionResults.length"
                @click="viewAllVision"
                class="view-all-button"
              >
                View all
                <ArrowRight class="w-3 h-3" />
              </button>
            </div>
            
            <div class="section-items">
              <div
                v-for="(result, index) in visionResults"
                :key="'vis-' + index"
                @click="selectVision(result)"
                @mouseenter="hoveredIndex = 'vis-' + index"
                :class="{ hovered: hoveredIndex === 'vis-' + index }"
                class="result-item"
              >
                <div class="item-icon">
                  <Image class="w-4 h-4" />
                </div>
                <div class="item-content">
                  <span class="item-text">{{ result.text }}</span>
                  <div class="item-meta">
                    <span class="meta-session">{{ formatJobId(result.job_id) }}</span>
                    <span class="meta-separator">•</span>
                    <span class="meta-frame">{{ result.frame }}</span>
                  </div>
                </div>
                <div class="item-action">
                  <CornerDownLeft class="w-3 h-3" />
                </div>
              </div>
            </div>
          </div>

          <!-- Sessions Section -->
          <div v-if="sessionResults.length > 0" class="result-section">
            <div class="section-header">
              <div class="section-title">
                <Video class="w-4 h-4" />
                <span>SESSIONS</span>
                <span class="section-count">({{ sessionsTotal }})</span>
              </div>
            </div>
            
            <div class="section-items">
              <div
                v-for="(result, index) in sessionResults"
                :key="'ses-' + index"
                @click="selectSession(result)"
                @mouseenter="hoveredIndex = 'ses-' + index"
                :class="{ hovered: hoveredIndex === 'ses-' + index }"
                class="result-item"
              >
                <div class="item-icon">
                  <PlayCircle class="w-4 h-4" />
                </div>
                <div class="item-content">
                  <div class="item-text">{{ result.job_id }}</div>
                  <div class="item-meta">
                    <span :class="`tag tag-${result.protocol.toLowerCase()}`">
                      {{ result.protocol }}
                    </span>
                    <span class="meta-separator">•</span>
                    <span class="meta-duration">{{ result.duration }}</span>
                  </div>
                </div>
                <div class="item-action">
                  <CornerDownLeft class="w-3 h-3" />
                </div>
              </div>
            </div>
          </div>

          <!-- Events Section -->
          <div v-if="eventResults.length > 0" class="result-section">
            <div class="section-header">
              <div class="section-title">
                <Activity class="w-4 h-4" />
                <span>EVENTS</span>
                <span class="section-count">({{ eventsTotal }})</span>
              </div>
            </div>
            
            <div class="section-items">
              <div
                v-for="(result, index) in eventResults"
                :key="'evt-' + index"
                @click="selectEvent(result)"
                @mouseenter="hoveredIndex = 'evt-' + index"
                :class="{ hovered: hoveredIndex === 'evt-' + index }"
                class="result-item"
              >
                <div class="item-icon">
                  <Zap class="w-4 h-4" />
                </div>
                <div class="item-content">
                  <span class="item-text">{{ result.description }}</span>
                  <div class="item-meta">
                    <span class="meta-session">{{ formatJobId(result.job_id) }}</span>
                    <span class="meta-separator">•</span>
                    <span class="meta-count">{{ result.eventCount }} events</span>
                  </div>
                </div>
                <div class="item-action">
                  <CornerDownLeft class="w-3 h-3" />
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer with shortcuts -->
        <div v-if="hasAnyResults" class="results-footer">
          <div class="footer-hint">
            <kbd>↑</kbd>
            <kbd>↓</kbd>
            <span>Navigate</span>
          </div>
          <div class="footer-hint">
            <kbd>Enter</kbd>
            <span>Select</span>
          </div>
          <div class="footer-hint">
            <kbd>Esc</kbd>
            <span>Close</span>
          </div>
        </div>

      </div>
    </transition>

  </div>
</template>

<script setup>
/*
  File: src/components/GlobalSearch.vue
*/

import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useSessionStore } from "../stores/sessionStore";

/* Lucide icons */
import {
  Search,
  X,
  AlertTriangle,
  SearchX,
  Terminal,
  ArrowRight,
  ChevronRight,
  CornerDownLeft,
  Eye,
  Image,
  Video,
  PlayCircle,
  Zap,
  Activity,
} from "lucide-vue-next";

import {
  globalSearch,
  formatTimestamp,
  formatJobId,
  saveSearchHistory,
} from "../services/globalSearchService";

/* Detect MacOS */
const isMac = computed(() => navigator.platform.toLowerCase().includes("mac"));

/* Setup */
const router = useRouter();
const sessions = useSessionStore();

const wrapper = ref(null);
const searchInput = ref(null);

/* State */
const query = ref("");
const isFocused = ref(false);
const loading = ref(false);
const searchError = ref(null);
const results = ref(null);
const hoveredIndex = ref(null);

let searchDebounce = null;
let blurTimeout = null;

/* Computed */
const showResults = computed(() => {
  return (
    isFocused.value &&
    (query.value.length >= 2 || loading.value || searchError.value)
  );
});

const commandResults = computed(() => {
  const data = results.value?.results?.find((r) => r.type === "commands");
  return data?.results || [];
});

const commandsTotal = computed(() => {
  const data = results.value?.results?.find((r) => r.type === "commands");
  return data?.total || 0;
});

const visionResults = computed(() => {
  const data = results.value?.results?.find((r) => r.type === "vision");
  return data?.results || [];
});

const visionTotal = computed(() => {
  const data = results.value?.results?.find((r) => r.type === "vision");
  return data?.total || 0;
});

const sessionResults = computed(() => {
  const data = results.value?.results?.find((r) => r.type === "sessions");
  return data?.results || [];
});

const sessionsTotal = computed(() => {
  const data = results.value?.results?.find((r) => r.type === "sessions");
  return data?.total || 0;
});

const eventResults = computed(() => {
  const data = results.value?.results?.find((r) => r.type === "events");
  return data?.results || [];
});

const eventsTotal = computed(() => {
  const data = results.value?.results?.find((r) => r.type === "events");
  return data?.total || 0;
});

const hasAnyResults = computed(() => {
  return (
    commandResults.value.length > 0 ||
    visionResults.value.length > 0 ||
    sessionResults.value.length > 0 ||
    eventResults.value.length > 0
  );
});

/* Input Handlers */
function onInput() {
  if (query.value.length < 2) {
    results.value = null;
    searchError.value = null;
    return;
  }

  if (searchDebounce) clearTimeout(searchDebounce);

  loading.value = true;
  searchError.value = null;

  searchDebounce = setTimeout(() => performSearch(), 300);
}

async function performSearch() {
  try {
    const searchResults = await globalSearch(sessions.getApiBase, query.value, {
      includeCommands: true,
      includeVision: false,
      includeSessions: true,
      includeEvents: false,
      limit: 5,
      sessions: sessions.sessions,
    });

    results.value = searchResults;
    saveSearchHistory(query.value, searchResults.totalResults);
  } catch (err) {
    searchError.value = err.message;
    results.value = null;
  } finally {
    loading.value = false;
  }
}

function retry() {
  searchError.value = null;
  performSearch();
}

function onFocus() {
  isFocused.value = true;
  if (blurTimeout) clearTimeout(blurTimeout);
}

function onBlur() {
  blurTimeout = setTimeout(() => {
    isFocused.value = false;
    hoveredIndex.value = null;
  }, 200);
}

function clear() {
  query.value = "";
  results.value = null;
  searchError.value = null;
  hoveredIndex.value = null;
  searchInput.value?.focus();
}

function onKeyDown(e) {
  if (e.key === "Escape") {
    e.preventDefault();
    clear();
    searchInput.value?.blur();
    return;
  }
}

/* Result selection */
function selectCommand(result) {
  const job = sessions.sessions.find((s) => s.job_id === result.job_id);

  if (job) {
    sessions.selectSession(job);

    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("player-seek", {
          detail: { time: Math.max(0, result.timestamp - 1) },
        })
      );
    }, 500);

    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("change-analysis-tab", {
          detail: { tab: "commands" },
        })
      );
    }, 600);
  }

  clear();
  searchInput.value?.blur();
}

function selectVision(result) {
  const job = sessions.sessions.find((s) => s.job_id === result.job_id);

  if (job) {
    sessions.selectSession(job);

    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("change-analysis-tab", {
          detail: { tab: "vision" },
        })
      );
    }, 500);
  }

  clear();
  searchInput.value?.blur();
}

function selectSession(result) {
  const job = sessions.sessions.find((s) => s.job_id === result.job_id);

  if (job) sessions.selectSession(job);

  clear();
  searchInput.value?.blur();
}

function selectEvent(result) {
  const job = sessions.sessions.find((s) => s.job_id === result.job_id);

  if (job) {
    sessions.selectSession(job);

    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("change-analysis-tab", {
          detail: { tab: "activity" },
        })
      );
    }, 500);
  }

  clear();
  searchInput.value?.blur();
}

function viewAllCommands() {
  window.dispatchEvent(
    new CustomEvent("change-analysis-tab", { detail: { tab: "commands" } })
  );
  clear();
  searchInput.value?.blur();
}

function viewAllVision() {
  window.dispatchEvent(
    new CustomEvent("change-analysis-tab", { detail: { tab: "vision" } })
  );
  clear();
  searchInput.value?.blur();
}

/* Global shortcut */
function handleGlobalShortcut(e) {
  if ((e.ctrlKey || e.metaKey) && e.key === "k") {
    e.preventDefault();
    searchInput.value?.focus();
  }
}

/* Lifecycle */
onMounted(() => {
  window.addEventListener("keydown", handleGlobalShortcut);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleGlobalShortcut);

  if (searchDebounce) clearTimeout(searchDebounce);
  if (blurTimeout) clearTimeout(blurTimeout);
});
</script>

<style scoped>
/* ============================================
   SEARCH CONTAINER
   ============================================ */
.global-search-wrapper {
  position: relative;
  width: 100%;
  max-width: 42rem; /* 672px */
  z-index: 50;
}

.search-container {
  position: relative;
  width: 100%;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

/* ============================================
   SEARCH INPUT
   ============================================ */
.search-input {
  width: 100%;
  height: 2.5rem; /* 40px */
  padding: 0.625rem 8rem 0.625rem 2.5rem;
  
  background-color: var(--bg-quaternary);
  border: 1px solid var(--border-color);
  border-radius: 0.375rem; /* 6px */
  
  font-size: 0.875rem; /* 14px */
  font-weight: 400;
  color: var(--text-primary);
  
  transition: all 0.2s ease;
}

.search-input::placeholder {
  color: var(--text-secondary);
}

.search-input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
}

/* ============================================
   SEARCH ICONS
   ============================================ */
.search-icon {
  position: absolute;
  left: 0.75rem;
  width: 1rem;
  height: 1rem;
  color: var(--text-secondary);
  pointer-events: none;
}

.search-input:focus ~ .search-icon {
  color: var(--accent);
}

/* ============================================
   LOADING & CLEAR BUTTONS
   ============================================ */
.search-loading {
  position: absolute;
  right: 0.75rem;
  display: flex;
  align-items: center;
}

.spinner-small {
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--bg-quaternary);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.search-clear {
  position: absolute;
  right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;
}

.search-clear:hover {
  background: var(--hover-bg);
  color: var(--text-primary);
}

/* ============================================
   SHORTCUT HINT
   ============================================ */
.shortcut-hint {
  position: absolute;
  right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  pointer-events: none;
}

.shortcut-hint kbd {
  padding: 0.125rem 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.25rem;
}

/* ============================================
   RESULTS DROPDOWN
   ============================================ */
.results-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  
  max-height: 32rem; /* 512px */
  overflow-y: auto;
  
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  
  z-index: 100;
}

/* Dropdown animation */
.dropdown-enter-active {
  transition: all 0.2s ease-out;
}

.dropdown-leave-active {
  transition: all 0.15s ease-in;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-0.5rem);
}

.dropdown-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

/* ============================================
   LOADING STATE
   ============================================ */
.result-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.spinner-large {
  width: 2rem;
  height: 2rem;
  border: 3px solid var(--bg-quaternary);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ============================================
   ERROR STATE
   ============================================ */
.result-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.retry-button:hover {
  background: var(--bg-quaternary);
  border-color: var(--accent);
}

/* ============================================
   EMPTY STATE
   ============================================ */
.result-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

/* ============================================
   RESULTS CONTENT
   ============================================ */
.results-content {
  padding: 0.5rem;
}

.result-section {
  margin-bottom: 0.75rem;
}

.result-section:last-child {
  margin-bottom: 0;
}

/* ============================================
   SECTION HEADER
   ============================================ */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.25rem;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.section-title i {
  width: 1rem;
  height: 1rem;
}

.section-count {
  font-weight: 600;
  color: var(--text-tertiary);
}

.view-all-button {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--accent);
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.view-all-button:hover {
  background: rgba(var(--accent-rgb), 0.1);
}

.view-all-button i {
  width: 0.75rem;
  height: 0.75rem;
}

/* ============================================
   SECTION ITEMS
   ============================================ */
.section-items {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

/* ============================================
   RESULT ITEM
   ============================================ */
.result-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  
  background: var(--bg-tertiary);
  border: 1px solid transparent;
  border-radius: 0.375rem;
  
  cursor: pointer;
  transition: all 0.15s ease;
}

.result-item:hover,
.result-item.hovered {
  background: var(--bg-quaternary);
  border-color: var(--accent);
  box-shadow: 0 0 0 1px var(--accent);
}

/* Item icon */
.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  color: var(--accent);
}

/* Item content */
.item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.item-command,
.item-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-command {
  font-family: 'Consolas', 'Monaco', monospace;
  color: var(--success-color);
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.meta-session,
.meta-time,
.meta-duration,
.meta-frame,
.meta-count {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
}

.meta-separator {
  color: var(--text-tertiary);
}

/* Tags */
.tag {
  padding: 0.125rem 0.5rem;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 0.25rem;
  border: 1px solid;
}

.tag-rdp {
  background: rgba(86, 156, 214, 0.1);
  color: #569cd6;
  border-color: #569cd6;
}

.tag-vnc {
  background: rgba(197, 134, 192, 0.1);
  color: #c586c0;
  border-color: #c586c0;
}

.tag-ssh {
  background: rgba(78, 201, 176, 0.1);
  color: #4ec9b0;
  border-color: #4ec9b0;
}

/* Item action icon */
.item-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: var(--text-tertiary);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.result-item:hover .item-action,
.result-item.hovered .item-action {
  opacity: 1;
}

/* ============================================
   RESULTS FOOTER
   ============================================ */
.results-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 0.75rem;
  border-top: 1px solid var(--border-color);
  background: var(--bg-tertiary);
}

.footer-hint {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.footer-hint kbd {
  min-width: 1.5rem;
  padding: 0.125rem 0.375rem;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-quaternary);
  border: 1px solid var(--border-color);
  border-radius: 0.25rem;
}

/* ============================================
   CUSTOM SCROLLBAR
   ============================================ */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  transition: background 0.2s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}
</style>